/**
 * Helper simple pour gérer le contexte Programme/Lot via les query params
 * L'URL devient la source de vérité au lieu du store
 */

/**
 * Récupère l'ID de l'annonce parente depuis l'URL
 */
export function getAnnonceParenteFromRoute(route) {
  // Nouvelle route spécifique aux lots
  if (route && route.name === 'posts-lots' && route.params.programId) {
    return route.params.programId
  }
  // Fallback pour l'ancienne méthode (compatibilité)
  return (route.query && route.query.annonceParente) || null
}

/**
 * Vérifie si on est dans un contexte de lots (annonce parente présente ou route posts-lots)
 */
export function isInLotsContext(route) {
  // Nouvelle route spécifique aux lots
  if (route && route.name === 'posts-lots') {
    return true
  }
  // Fallback pour l'ancienne méthode (compatibilité)
  return !!route.query.annonceParente
}

/**
 * Vérifie si on est dans un contexte de programmes (pas d'annonce parente)
 */
export function isInProgramsContext(route) {
  return !route.query.annonceParente
}

/**
 * Détermine si on est dans le contexte de création/édition d'un programme
 * (typeObjet programme ET pas d'annonce parente dans l'URL)
 */
export function isInProgramCreationContext(route, post) {
  if (!post || !post.typeObjet) return false
  
  const isProgramType = post.typeObjet >= 3000 && post.typeObjet < 4000
  const hasAnnonceParente = !!route.query.annonceParente
  
  return isProgramType && !hasAnnonceParente
}

/**
 * Détermine si on est dans le contexte de création/édition d'un lot
 * (présence d'annonce parente dans l'URL)
 */
export function isInLotCreationContext(route) {
  return !!route.query.annonceParente
}

/**
 * Détermine la valeur du filtre creationProgramme basé sur l'URL et le post
 * - true : on crée/édite un programme
 * - false : on est dans le contexte des lots
 * - null : autres cas (immo ancien, etc.)
 */
export function getCreationProgrammeFromRoute(route, post, userType) {
  // Seulement pour les promoteurs et bailleurs sociaux
  if (userType !== 'PROMOTEUR' && userType !== 'BAILLEUR_SOCIAL') {
    return null
  }
  
  if (isInLotCreationContext(route)) {
    return false // On est dans les lots
  }
  
  // Pour la création, vérifier d'abord le paramètre URL
  if (!post || !post.id) {
    // Cas de création - vérifier le paramètre type dans l'URL
    if (route.query.type === 'program') {
      return true // Intention explicite de créer un programme
    }
    return null // Création normale (immo ancien)
  }
  
  // Pour l'édition, utiliser le typeObjet du post
  if (post && isInProgramCreationContext(route, post)) {
    return true // On crée/édite un programme
  }
  
  // Sur la liste des programmes (pas d'annonce parente)
  if (route.name === 'posts' && isInProgramsContext(route)) {
    return null // Liste des programmes, pas de création
  }
  
  return null // Autres cas
}

/**
 * Génère l'URL pour gérer les lots d'un programme
 */
export function getLotsUrl(programId) {
  return {
    name: 'posts-lots',
    params: { programId: programId }
  }
}

/**
 * Génère l'URL pour retourner aux programmes
 */
export function getProgramsUrl() {
  return {
    name: 'posts'
    // Pas de query params = programmes
  }
}

/**
 * Génère l'URL d'édition avec le bon contexte
 */
export function getEditUrl(postId, annonceParente = null, sourceCode = null) {
  const route = {
    name: 'post-edit',
    params: { id: postId }
  }
  
  if (annonceParente) {
    route.query = { annonceParente }
  }
  
  if (sourceCode && route.query) {
    route.query.saisie = sourceCode
  } else if (sourceCode) {
    route.query = { saisie: sourceCode }
  }
  
  return route
}

/**
 * Génère l'URL de création avec le bon contexte
 */
export function getCreateUrl(annonceParente = null) {
  const route = {
    name: 'post-new'
  }
  
  if (annonceParente) {
    route.query = { annonceParente }
  }
  
  return route
}

/**
 * Génère l'URL de création d'un programme
 */
export function getCreateProgramUrl() {
  return {
    name: 'post-new',
    query: { type: 'program' }
  }
}

/**
 * Détermine l'URL de redirection après sauvegarde basée sur le contexte
 */
export function getRedirectAfterSaveUrl(route) {
  const annonceParente = getAnnonceParenteFromRoute(route)
  
  if (annonceParente) {
    // On créait/éditait un lot → retourner à la liste des lots
    return getLotsUrl(annonceParente)
  } else {
    // On créait/éditait un programme ou immo ancien → retourner à la liste par défaut
    return getProgramsUrl()
  }
}

/**
 * Synchronise le store avec l'URL (appelé dans les composants)
 * Uniquement pour les promoteurs et bailleurs sociaux
 */
export function syncStoreWithRoute(route, store, post = null) {
  const sitimmoUser = store.getters.getSitimmoUser
  const isPromoteur = sitimmoUser && sitimmoUser.metier === 'PROMOTEUR'
  const isBailleurSocial = sitimmoUser && sitimmoUser.metier === 'BAILLEUR_SOCIAL'
  
  // Ne synchroniser que pour les promoteurs et bailleurs sociaux
  if (!isPromoteur && !isBailleurSocial) {
    return
  }
  
  const annonceParente = getAnnonceParenteFromRoute(route)
  
  // Mettre à jour le store seulement si différent
  if (store.state.postsListImmo.filter.annonceParente !== annonceParente) {
    store.commit('postsListImmo/SET_ANNONCE_PARENTE', annonceParente)
  }
} 